import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { isBusy, subscribeBusy } from './busy'

// One-shot guard so a genuinely broken build can't reload-loop; sessionStorage access is
// wrapped because some privacy modes throw on write (then we just skip the auto-reload).
const RELOADED_KEY = 'reptree-chunk-reloaded'
const flag = {
  get: () => { try { return sessionStorage.getItem(RELOADED_KEY) != null } catch { return true } },
  set: () => { try { sessionStorage.setItem(RELOADED_KEY, '1') } catch { /* skip auto-reload */ } },
  clear: () => { try { sessionStorage.removeItem(RELOADED_KEY) } catch { /* ignore */ } },
}

/**
 * React.lazy with stale-chunk recovery. The service worker updates mid-session (skipWaiting and
 * clientsClaim, so users get deploys without closing tabs) and prunes the old precache. A page that
 * stays open on the old build (the person chose "Later" on the update notice) can then lazy-import an
 * old hashed chunk that is gone from the precache and the server. It usually still loads from the HTTP
 * cache; when it does not, the dynamic import rejects and the route would render nothing. Recovery:
 * reload once. The shell is network-first, so the reload boots the live build and the navigation the
 * person asked for just works. The reload waits until any upload finishes (reloadWhenIdle): reloading
 * mid-upload would fight the leave-page prompt lib/busy.ts arms, and a person who cancels it would strand
 * this route on a pending import. A second failure surfaces the real error.
 */
/** Reload to pick up the live build, but never while an upload is in flight: wait for the gate to clear so
 *  the reload does not collide with the leave-page prompt. Re-checks after subscribing to close the gap where
 *  the last hold releases between the check and the subscription. */
function reloadWhenIdle(): void {
  if (!isBusy()) return void location.reload()
  const off = subscribeBusy(() => {
    if (!isBusy()) {
      off()
      location.reload()
    }
  })
  if (!isBusy()) {
    off()
    location.reload()
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().then(
      (m) => {
        flag.clear() // healthy load — re-arm the one-shot for the next deploy
        return m
      },
      (err: unknown) => {
        if (flag.get()) throw err // already reloaded once, a real failure, surface it
        flag.set()
        reloadWhenIdle()
        return new Promise<never>(() => {}) // the reload IS the resolution; never settle
      },
    ),
  )
}
